import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../utils/api'
import { SettingsPageTemplate } from '../components/SettingsPageTemplate'

interface ClosingFeeTier {
  min_amount: number
  fee: number
}

interface FinancialSettingsData {
  agency_fee_percentage: string
  closing_fee_tiers: ClosingFeeTier[]
}

function getClosingFeeForAmount(amount: number, tiers: ClosingFeeTier[]): number {
  if (!tiers || tiers.length === 0) return 0
  const sorted = [...tiers].sort((a, b) => b.min_amount - a.min_amount)
  const match = sorted.find(t => amount >= t.min_amount)
  return match !== undefined ? match.fee : 0
}

const FinancialSettings: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [agencyFeePercentage, setAgencyFeePercentage] = useState('')
  const [tiers, setTiers] = useState<ClosingFeeTier[]>([{ min_amount: 0, fee: 0 }])

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text })
    setTimeout(() => setMessage(null), 5000)
  }

  useEffect(() => {
    fetchFinancialSettings()
  }, [])

  const fetchFinancialSettings = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) { setLoading(false); return }

      const response = await fetch(`${API_BASE_URL}/api/settings/financial`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        const settingsArray: Array<{ setting_key: string; setting_value: string }> = data.settings || []

        const agencyRaw = settingsArray.find(s => s.setting_key === 'agency_fee_percentage')?.setting_value || ''
        setAgencyFeePercentage(agencyRaw)

        const tiersRaw = settingsArray.find(s => s.setting_key === 'closing_fee_tiers')?.setting_value
        let parsedTiers: ClosingFeeTier[] = []
        if (tiersRaw) {
          try { parsedTiers = JSON.parse(tiersRaw) } catch { /* ignore */ }
        }

        if (!parsedTiers || parsedTiers.length === 0) {
          const flatFeeRaw = settingsArray.find(s => s.setting_key === 'closing_fee_amount')?.setting_value
          const flatFee = parseFloat(flatFeeRaw || '0') || 0
          parsedTiers = [{ min_amount: 0, fee: flatFee }]
        }

        setTiers(parsedTiers)
      }
    } catch (error) {
      console.error('Error fetching financial settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()

    const sortedTiers = [...tiers].sort((a, b) => a.min_amount - b.min_amount)
    if (sortedTiers.length > 0 && sortedTiers[0]!.min_amount !== 0) {
      showMessage('error', t('financialSettings.firstTierMustBeZero'))
      return
    }

    setSaving(true)
    try {
      const token = localStorage.getItem('token')
      const payload: Record<string, string> = {
        agency_fee_percentage: agencyFeePercentage,
        closing_fee_tiers: JSON.stringify(sortedTiers),
        closing_fee_amount: String(sortedTiers[0]?.fee ?? 0)
      }

      const response = await fetch(`${API_BASE_URL}/api/settings/financial`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        showMessage('success', t('financialSettings.messages.settingsSaved'))
      } else {
        const data = await response.json()
        showMessage('error', data.message || t('financialSettings.messages.failedToSave'))
      }
    } catch (error) {
      showMessage('error', t('financialSettings.messages.failedToSave'))
    } finally {
      setSaving(false)
    }
  }

  const addTier = () => {
    const sorted = [...tiers].sort((a, b) => a.min_amount - b.min_amount)
    const lastMin = sorted[sorted.length - 1]?.min_amount ?? 0
    setTiers([...sorted, { min_amount: lastMin + 100000, fee: 0 }])
  }

  const removeTier = (index: number) => {
    if (tiers.length <= 1) return
    const sorted = [...tiers].sort((a, b) => a.min_amount - b.min_amount)
    setTiers(sorted.filter((_, i) => i !== index))
  }

  const updateTier = (index: number, field: keyof ClosingFeeTier, value: number) => {
    const sorted = [...tiers].sort((a, b) => a.min_amount - b.min_amount)
    const updated = [...sorted]
    updated[index] = { ...updated[index]!, [field]: value }
    setTiers(updated)
  }

  const sortedTiers = [...tiers].sort((a, b) => a.min_amount - b.min_amount)

  return (
    <SettingsPageTemplate
      title={t('financialSettings.title')}
      description={t('financialSettings.description')}
      message={message}
      loading={loading}
    >
      <form onSubmit={handleSaveSettings}>
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          {/* Left column — configuration */}
          <div className="xl:col-span-3 space-y-6">

            {/* Agency Fee */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <svg className="w-4 h-4 text-violet-600 dark:text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('financialSettings.agencyFeeTitle')}</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{t('financialSettings.agencyFeeSubtitle')}</p>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <input
                      type="number"
                      value={agencyFeePercentage}
                      onChange={e => setAgencyFeePercentage(e.target.value)}
                      className="w-full pl-4 pr-10 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                      placeholder="e.g. 1.25"
                      step="0.01"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400">%</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('financialSettings.ofGrossCommission')}</span>
                </div>
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  {t('financialSettings.agencyFeeExample')}
                </p>
              </div>
            </div>

            {/* Closing Fee Tiers */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t('financialSettings.closingFeesTitle')}</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('financialSettings.closingFeesSubtitle')}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addTier}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('financialSettings.addTier')}
                </button>
              </div>

              <div className="p-6">
                {/* Column headers */}
                <div className="grid grid-cols-12 gap-3 mb-2 px-1">
                  <div className="col-span-5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('financialSettings.fromAmount')}</div>
                  <div className="col-span-5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('financialSettings.closingFeeColumn')}</div>
                  <div className="col-span-2" />
                </div>

                <div className="space-y-2">
                  {sortedTiers.map((tier, i) => (
                    <div key={i} className="grid grid-cols-12 gap-3 items-center group">
                      {/* Min amount */}
                      <div className="col-span-5 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                        <input
                          type="number"
                          value={tier.min_amount}
                          onChange={e => updateTier(i, 'min_amount', parseFloat(e.target.value) || 0)}
                          disabled={i === 0}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
                          placeholder="0"
                          min="0"
                          step="1000"
                        />
                        {i === 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2">
                            <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{t('financialSettings.baseBadge')}</span>
                          </span>
                        )}
                      </div>

                      {/* Closing fee */}
                      <div className="col-span-5 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                        <input
                          type="number"
                          value={tier.fee}
                          onChange={e => updateTier(i, 'fee', parseFloat(e.target.value) || 0)}
                          className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      {/* Remove */}
                      <div className="col-span-2 flex justify-end">
                        {tiers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTier(i)}
                            className="p-1.5 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 rounded-md transition"
                            title={t('financialSettings.removeTier')}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Range label */}
                      {sortedTiers.length > 1 && (
                        <div className="col-span-12 -mt-1 mb-1">
                          <p className="text-xs text-gray-400 dark:text-gray-500 pl-1">
                            {i < sortedTiers.length - 1
                              ? t('financialSettings.tierRangeUpTo', {
                                  from: `$${tier.min_amount.toLocaleString()}`,
                                  to: `$${(sortedTiers[i + 1]!.min_amount - 1).toLocaleString()}`
                                })
                              : t('financialSettings.tierRangeAndAbove', {
                                  from: `$${tier.min_amount.toLocaleString()}`
                                })
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {sortedTiers.length === 1 && (
                  <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                    {t('financialSettings.singleTierNote')}
                  </p>
                )}
              </div>
            </div>

            {/* Save button */}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 px-6 rounded-xl font-medium text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 transition shadow-sm"
            >
              {saving ? t('financialSettings.saving') : t('financialSettings.saveFinancialSettings')}
            </button>
          </div>

          {/* Right column — live preview */}
          <div className="xl:col-span-2 space-y-6">
            <CommissionCalculator
              agencyFeePercentage={parseFloat(agencyFeePercentage || '0') || 0}
              tiers={sortedTiers}
            />

            {/* Formula reference */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/60 dark:to-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('financialSettings.howItsCalculated')}
              </h3>
              <ol className="space-y-1.5 text-xs text-gray-500 dark:text-gray-400">
                <li className="flex gap-2">
                  <span className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">1</span>
                  <span dangerouslySetInnerHTML={{ __html: t('financialSettings.formulaGross').replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-700 dark:text-gray-300">$1</strong>') }} />
                </li>
                <li className="flex gap-2">
                  <span className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">2</span>
                  <span dangerouslySetInnerHTML={{ __html: t('financialSettings.formulaAgencyFee').replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-700 dark:text-gray-300">$1</strong>') }} />
                </li>
                <li className="flex gap-2">
                  <span className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">3</span>
                  <span dangerouslySetInnerHTML={{ __html: t('financialSettings.formulaClosingFee').replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-700 dark:text-gray-300">$1</strong>') }} />
                </li>
                <li className="flex gap-2">
                  <span className="w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">4</span>
                  <span dangerouslySetInnerHTML={{ __html: t('financialSettings.formulaNet').replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-700 dark:text-gray-300">$1</strong>') }} />
                </li>
              </ol>
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">{t('financialSettings.pointsNote')}</p>
            </div>
          </div>
        </div>
      </form>
    </SettingsPageTemplate>
  )
}

// ─── Commission Calculator ────────────────────────────────────────────────────

interface CommissionCalculatorProps {
  agencyFeePercentage: number
  tiers: ClosingFeeTier[]
}

const CommissionCalculator: React.FC<CommissionCalculatorProps> = ({ agencyFeePercentage, tiers }) => {
  const { t } = useTranslation()
  const [dealAmount, setDealAmount] = useState('500000')
  const [points, setPoints] = useState('100')

  const amount = parseFloat(dealAmount || '0') || 0
  const pts = parseFloat(points || '0') || 0

  const closingFee = getClosingFeeForAmount(amount, tiers)

  const result = (() => {
    if (amount <= 0 || pts < 0) return null
    const gross = amount * (pts / 10000)
    const agencyFeeAmt = gross * (agencyFeePercentage / 100)
    const net = gross - agencyFeeAmt - closingFee
    return { gross, agencyFeeAmt, closingFee, net }
  })()

  const matchedTier = tiers.length > 0
    ? [...tiers].sort((a, b) => b.min_amount - a.min_amount).find(t => amount >= t.min_amount)
    : null

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('financialSettings.commissionCalculatorTitle')}</h2>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('financialSettings.loanAmount')}</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
              <input
                type="number"
                value={dealAmount}
                onChange={e => setDealAmount(e.target.value)}
                className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                placeholder="500000"
                step="10000"
                min="0"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{t('financialSettings.points')}</label>
            <input
              type="number"
              value={points}
              onChange={e => setPoints(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
              placeholder="100"
              step="1"
              min="0"
              max="999"
            />
            <p className="mt-1 text-xs text-gray-400">{pts > 0 ? `${(pts / 100).toFixed(2)}%` : '0%'}</p>
          </div>
        </div>

        {/* Active tier badge */}
        {tiers.length > 1 && amount > 0 && matchedTier && (
          <div className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-1.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t('financialSettings.tierApplied', {
              from: `$${matchedTier.min_amount.toLocaleString()}`,
              fee: `$${matchedTier.fee.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
            })}
          </div>
        )}

        {result ? (
          <div className="rounded-xl bg-gray-50 dark:bg-gray-700/50 divide-y divide-gray-100 dark:divide-gray-600 overflow-hidden">
            <div className="flex justify-between items-center px-4 py-2.5 text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('financialSettings.grossLabel', { pct: (pts / 100).toFixed(2) })}</span>
              <span className="font-medium text-gray-800 dark:text-gray-200">
                ${result.gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-2.5 text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('financialSettings.agencyFeeLabel', { pct: agencyFeePercentage })}</span>
              <span className="text-red-500 dark:text-red-400">
                −${result.agencyFeeAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-2.5 text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('financialSettings.closingFeeLabel')}</span>
              <span className="text-red-500 dark:text-red-400">
                −${result.closingFee.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 bg-white dark:bg-gray-700">
              <span className="font-semibold text-gray-900 dark:text-white text-sm">{t('financialSettings.netCommissionLabel')}</span>
              <span className={`text-lg font-bold ${result.net >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                ${Math.max(0, result.net).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-sm text-gray-400 dark:text-gray-500">
            {t('financialSettings.enterAmountBreakdown')}
          </div>
        )}
      </div>
    </div>
  )
}

export default FinancialSettings
